import {
  ISystemSetupCombinationValue,
  instanceOfEnumerationValueDto,
  instanceOfNumericValueDto,
  instanceOfFaultyNumericValueDto,
  instanceOfDiscreteNumericValueDto,
  instanceOfTextValueDto,
  instanceOfBeamShapingGroupCircularDto,
  instanceOfBeamShapingGroupRectangularDto,
  ParameterCombinationDto,
  EvaluationParameterScopeDto,
  EnumerationValueDto,
  FaultyNumericValueDto,
  DiscreteNumericValueDto,
  NumericValueDto,
  TextValueDto,
} from '@/api/generator';
import { Clause, clauseError, ParamInfo, parseCondition } from './condition';
import { getFormattingOptionsFromNumericValue } from '@/utils/numberUtils';
import { translateEntityProperty, translateGroupName, truthy } from '@/utils/helper';
import { getSetupParameterName } from '@/views/tasks/utils';
import { t } from 'i18next';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import { isNumber, sortBy, uniq, uniqBy } from 'lodash';
import { apply, RulesLogic, AdditionalOperation } from 'json-logic-js';

// Label with a qualifier for parameters from a beam quality, beam shaping or QA group, which repeat the names of other containers.
export function getParamLabel(id: string, combinations?: ParameterCombinationDto[]): string {
  const origin = combinations
    ?.flatMap(flattenWithQualifier)
    .find((o) => o.param.setupParameterId === id);
  if (!origin) return '';
  const name = getSetupParameterName(origin.param);

  return origin.qualifier ? `${name} (${origin.qualifier})` : name;
}

export function findParam(id: string, combinations?: ParameterCombinationDto[]) {
  return combinations?.flatMap(flattenCombination).find((param) => param.setupParameterId === id);
}

export function getParamIds(combinations?: ParameterCombinationDto[]): string[] {
  return uniq(
    combinations?.flatMap((combination) =>
      (combination.conditionValueAssignments ?? []).map((a) => a.setupParameterId),
    ),
  );
}

export function isParamAvailable(id: string, combinations?: ParameterCombinationDto[]): boolean {
  return getParamIds(combinations).includes(id);
}

// Shared across areas (setupParameterIds are GUIDs); cleared by resetParamInfoCache().
const paramInfoCache = new Map<string, ParamInfo>();

// An enum keeps the options an earlier info offered, so a removed option keeps its title; new titles win.
export function cacheParamInfo(id: string, info: ParamInfo): void {
  const cached = paramInfoCache.get(id);
  if (info.type === 'enum' && cached?.type === 'enum') {
    paramInfoCache.set(id, {
      ...info,
      options: uniqBy([...info.options, ...cached.options], (option) => option.value),
    });
    return;
  }
  paramInfoCache.set(id, info);
}

/** Title of an enum option, also for an option the setup no longer offers. */
export function enumValueTitle(id: string, value: Clause['value']): string | undefined {
  const info = paramInfoCache.get(id);

  return info?.type === 'enum'
    ? info.options.find((option) => option.value === value)?.title
    : undefined;
}

export function getCachedParamInfo(id: string): ParamInfo | undefined {
  return paramInfoCache.get(id);
}

export function resetParamInfoCache(): void {
  paramInfoCache.clear();
}

export function getParamInfo(
  param: PrimitiveParamType,
  combinations: ParameterCombinationDto[],
  label: string,
): ParamInfo {
  if (isEnumParamType(param)) {
    const options = uniqBy(
      combinations
        .flatMap(flattenCombination)
        .filter((p) => p.setupParameterId === param.setupParameterId)
        .filter(isEnumParamType)
        .filter(truthy)
        .map((p) => ({
          title: translateEntityProperty('name', p.value) || EMPTY_FIELD_SIGN,
          value: p.value?.id ?? null,
        })),
      (option) => option.value,
    );

    return { label, type: 'enum', options };
  }

  if (!isNumberParamType(param)) return { label, type: 'string' };

  const shared = { format: getFormattingOptionsFromNumericValue(param) };

  return instanceOfDiscreteNumericValueDto(param)
    ? { label, type: 'number', ...shared, availableValues: configuredValues(param, combinations) }
    : {
        label,
        type: 'number',
        ...shared,
        min: param.min ?? undefined,
        max: param.max ?? undefined,
      };
}

// The dto's own `availableValues` is not the selection: each combination carries one selected value.
function configuredValues(
  param: DiscreteNumericValueDto,
  combinations: ParameterCombinationDto[],
): number[] {
  return sortBy(
    uniq(
      combinations
        .flatMap(flattenCombination)
        .filter((p) => p.setupParameterId === param.setupParameterId)
        .filter(instanceOfDiscreteNumericValueDto)
        .map((p) => p.value)
        .filter(isNumber),
    ),
  );
}

function flattenCombination(combination: ParameterCombinationDto): PrimitiveParamType[] {
  return flattenWithQualifier(combination).map(({ param }) => param);
}

type ParamOrigin = { param: PrimitiveParamType; qualifier?: string };

function flattenWithQualifier(combination: ParameterCombinationDto): ParamOrigin[] {
  const qa = (combination.supportSystemValues ?? []).flatMap((value) =>
    toOrigins(value, (v) => translateGroupName(v.groupName ?? '') || t('DosimetryAndQaEquipment')),
  );
  const medical = (combination.targetSystemValues ?? []).flatMap((value) =>
    toOrigins(value, () => undefined),
  );

  return [...qa, ...medical];
}

function toOrigins(
  value: ISystemSetupCombinationValue,
  topLevelQualifier: (v: PrimitiveParamType) => string | undefined,
): ParamOrigin[] {
  if (isPrimitiveParamType(value)) {
    return [{ param: value, qualifier: topLevelQualifier(value) }];
  }

  // Nested groups (beam shaping, beam quality) hold their primitives as own properties.
  const qualifier =
    instanceOfBeamShapingGroupRectangularDto(value) || instanceOfBeamShapingGroupCircularDto(value)
      ? t('BeamShapingGroup')
      : t('BeamQuality');

  return Object.values(value)
    .filter(isPrimitiveParamType)
    .map((param) => ({ param, qualifier }));
}

// Evaluated exactly like the backend (EvaluationScopeConditionManager.SatisfiesCondition).
export function matchesCondition(
  condition: string | null | undefined,
  combination: ParameterCombinationDto,
): boolean {
  if (!condition) return false;

  try {
    const logic = JSON.parse(condition) as RulesLogic<AdditionalOperation>;

    return Boolean(apply(logic, combination));
  } catch {
    return false;
  }
}

// Param infos for every param the combinations still offer.
export function getParamInfos(combinations: ParameterCombinationDto[]): Map<string, ParamInfo> {
  const infos = new Map<string, ParamInfo>();
  getParamIds(combinations).forEach((paramId) => {
    const param = findParam(paramId, combinations);
    if (param?.name) {
      infos.set(paramId, getParamInfo(param, combinations, getParamLabel(paramId, combinations)));
    }
  });

  return infos;
}

export function isClauseApplicable(clause: Clause, paramIds: string[]): boolean {
  return !clause.param || paramIds.includes(clause.param);
}

export function clauseErrorFor(
  clause: Clause,
  paramInfos: Map<string, ParamInfo>,
  paramIds: string[],
): string | undefined {
  return clauseError(
    clause,
    clause.param ? paramInfos.get(clause.param) : undefined,
    isClauseApplicable(clause, paramIds),
  );
}

function firstClauseError(
  condition: string | null | undefined,
  paramInfos: Map<string, ParamInfo>,
  paramIds: string[],
): string | undefined {
  const clauses = parseCondition(condition ?? undefined);
  if (clauses === 'unsupported') return undefined;

  return clauses.map((clause) => clauseErrorFor(clause, paramInfos, paramIds)).find(Boolean);
}

/** First clause error of a condition against the combinations; unparseable conditions have none. */
export function conditionError(
  condition: string | null | undefined,
  combinations: ParameterCombinationDto[],
): string | undefined {
  return firstClauseError(condition, getParamInfos(combinations), getParamIds(combinations));
}

// Enabled scopes whose condition is valid and matches, per enabled combination, in scope order.
export function getAssignedScopesByCombination(
  scopes: EvaluationParameterScopeDto[] | null | undefined,
  combinations: ParameterCombinationDto[],
): Map<ParameterCombinationDto, EvaluationParameterScopeDto[]> {
  const paramInfos = getParamInfos(combinations);
  const paramIds = getParamIds(combinations);
  const validScopes = (scopes ?? []).filter(
    (scope) => scope.isEnabled && !firstClauseError(scope.condition, paramInfos, paramIds),
  );

  return new Map(
    combinations
      .filter((combination) => combination.isEnabled)
      .map((combination) => [
        combination,
        validScopes.filter((scope) => matchesCondition(scope.condition, combination)),
      ]),
  );
}

type EnumParamType = EnumerationValueDto;
function isEnumParamType(param: ISystemSetupCombinationValue | undefined): param is EnumParamType {
  return instanceOfEnumerationValueDto(param);
}

type NumberParamType = NumericValueDto | FaultyNumericValueDto | DiscreteNumericValueDto;
function isNumberParamType(
  param: ISystemSetupCombinationValue | undefined,
): param is NumberParamType {
  return (
    instanceOfNumericValueDto(param) ||
    instanceOfFaultyNumericValueDto(param) ||
    instanceOfDiscreteNumericValueDto(param)
  );
}

type StringParamType = TextValueDto;
function isStringParamType(
  param: ISystemSetupCombinationValue | undefined,
): param is StringParamType {
  return instanceOfTextValueDto(param);
}

type PrimitiveParamType = NumberParamType | EnumParamType | StringParamType;
function isPrimitiveParamType(
  param: ISystemSetupCombinationValue | undefined,
): param is PrimitiveParamType {
  return isNumberParamType(param) || isEnumParamType(param) || isStringParamType(param);
}
